import React from "react";
import "../../styles/timeline.css";
import CourseJourneyInfo from "../../components/CourseComp/CourseJourneyInfo";
import LessonTimeline from "../../components/CourseComp/LessonTimeline";
import { useCourseHook } from "./useCourseHook";
import { Navigate, useNavigate } from "react-router-dom";
import { Loading } from "../../components/common";
import SoftLockModal from "../../components/lessons/SoftLockModal";

const Course: React.FC = () => {
  const {
    courseId,
    courseDetails,
    lessons,
    isLoading,
    hasError,
    demoErrorCode,
    handleLessonClick,
    showSoftLockModal,
    softLockMessage,
    closeSoftLockModal,
  } = useCourseHook();
  const navigate = useNavigate()!;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading />
      </div>
    );
  }

  if (hasError) {
    return <Navigate to="/courseNotFound" replace />;
  }

  if (demoErrorCode === "course_retired") {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center">
        <h1 className="text-2xl font-semibold text-gray-800 mb-3">This course has been retired</h1>
        <p className="text-gray-600 mb-6">
          Your completed progress is preserved. Any certificates remain available in
          Certificates; eligible certified credentials remain in your Passport.
        </p>
        <button onClick={() => navigate("/courses")} className="text-primary hover:underline">
          Back to courses
        </button>
      </div>
    );
  }

  if (demoErrorCode === "demo_restricted") {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center">
        <h1 className="text-2xl font-semibold text-gray-800 mb-3">Course unavailable</h1>
        <p className="text-gray-600 mb-6">
          Demo access is no longer available.
        </p>
        <button
          onClick={() => navigate?.("/courses")}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          Back to courses
        </button>
      </div>
    );
  }

  if (demoErrorCode === "demo_expired") {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center">
        <h1 className="text-2xl font-semibold text-amber-800 mb-3">Demo access has ended</h1>
        <p className="text-amber-700 mb-6">
          Your demo period has finished. Contact Pedagogue to extend your access.
        </p>
        <a
          href="mailto:hello@pedagogue.io"
          className="rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
        >
          Email hello@pedagogue.io
        </a>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Section - Course Journey Info */}
          <CourseJourneyInfo
            courseTitle={courseDetails?.title || "Course"}
            courseIntro={
              courseDetails?.description || "Course description not available."
            }
            lessonsCount={lessons?.length || 0}
            courseData={courseDetails}
          />

          {/* Right Section - Lesson Timeline */}
          <LessonTimeline
            lessons={lessons || []}
            onLessonClick={handleLessonClick}
            courseId={courseId || ""}
            completedLessons={
              lessons?.filter((lesson) => lesson.status === "completed")
                .length || 0
            }
          />
        </div>
      </div>
      <SoftLockModal
        open={showSoftLockModal}
        message={
          softLockMessage ||
          "Thank you for your time and dedication to Module 1 of this course. The remaining half of this module will be unlocked on Tuesday 18th November, with content specifically customised to your organisation."
        }
        onClose={closeSoftLockModal}
      />
    </div>
  );
};

export default Course;
